// Task detail (RF-TASK-02). The board only carries TaskCard; the full task (description,
// acceptance checklist, server-computed actions and durations) is fetched on demand from
// GET /v1/tasks/{id}, and the timeline from GET /v1/tasks/{id}/timeline.
import { useEffect, useState } from "react";
import type { ProjectSummary, TaskAction, TaskCard } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { getTask } from "../../api/endpoints.ts";
import type { TaskDetail } from "../../api/types.ts";
import { RISK_VIEW, statusView } from "../../lib/status.ts";
import { DurationsView, TaskTimeline } from "../../lib/taskFacts.tsx";
import { Dialog } from "../../components/Dialog.tsx";
import { Button } from "../../components/Button.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { Pill, StatusPill } from "../../components/Status.tsx";
import { CheckIcon, CircleIcon } from "../../components/icons.tsx";
import { CardExecution, CardTimers } from "./TaskCardFacts.tsx";
import { candidateActions } from "./taskActions.ts";
import { useProjectRole } from "../../features/wave3/common.tsx";
import { ApprovalDecision } from "../../features/wave5/ApprovalDecision.tsx";
import { CouncilReviewsSection, TaskProductionSection } from "../../features/wave5/TaskProduction.tsx";
import { RoutingDecisionSection, RoutingSummary, useTaskRouting } from "../../features/routing/RoutingDecision.tsx";
import { BlockPanel } from "../../features/blocks/BlockPanel.tsx";

export function TaskDetailDialog({
  task,
  project,
  numbers,
  onClose,
  onAction,
  onDecided,
  onProjectChanged,
}: {
  task: TaskCard | null;
  project: ProjectSummary;
  numbers: Map<string, number>;
  onClose: () => void;
  onAction: (task: TaskCard, action: TaskAction) => void;
  /** A human decision was recorded (approved/rejected): the board is reloaded. */
  onDecided: () => void;
  /** The project itself changed (resumed from inside the decision): the project is reloaded too. */
  onProjectChanged: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  const canResume = useProjectRole(project.id).atLeast("maintainer");
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [detailError, setDetailError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const taskId = task?.id ?? null;
  const version = task?.version ?? 0;
  const routing = useTaskRouting(taskId, version);

  useEffect(() => {
    if (!taskId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailError(null);
    getTask(taskId)
      .then((r) => !cancelled && setDetail(r))
      .catch((e: unknown) => !cancelled && setDetailError(e));
    return () => {
      cancelled = true;
    };
  }, [taskId, version, attempt]);

  const current = detail && detail.id === taskId ? detail : null;

  return (
    <Dialog open={task !== null} onClose={onClose} wide title={task ? `#${task.number} · ${task.title}` : ""}>
      {task ? (
        <div className="stack task-detail">
          {task.status === "awaiting_human" ? (
            // Keyed by task: a fresh note/error state per task, never carried over from one decision to the next.
            <ApprovalDecision key={task.id} projectId={project.id} task={task} project={project} canResume={canResume} onDecided={onDecided} onProjectChanged={onProjectChanged} />
          ) : null}
          <div className="row row--wrap">
            <StatusPill view={statusView(task.status)} />
            {task.completion_kind === "existing" ? (
              <Pill tone="info" icon="check">
                {t("onboarding.existingPill")}
              </Pill>
            ) : null}
            {RISK_VIEW[task.risk] ? <StatusPill view={RISK_VIEW[task.risk]!} /> : null}
            <span className="badge">{t(`taskType.${task.type}`)}</span>
            <span className="badge">{t(`origin.${task.origin}`)}</span>
            <span className="badge">{t(`delivery.${task.delivery_target}`)}</span>
          </div>
          {task.status === "blocked" || (task.status === "failed" && task.block_reason) ? (
            // D37: translated reason, evidence, what waits for this task and the decisions (raw text under Technical details).
            <BlockPanel task={task} onChanged={onDecided} />
          ) : task.status_reason ? (
            <p className="task-card-blocker">{task.status_reason}</p>
          ) : null}
          <CardExecution task={task} />
          <CardTimers task={task} />
          <dl className="detail-grid">
            <div>
              <dt>{t("task.priority")}</dt>
              <dd>
                {task.priority_score !== null ? Math.round(task.priority_score) : "—"}
                {task.priority_reason ? <span className="subtle"> — {task.priority_reason}</span> : null}
              </dd>
            </div>
            <div>
              <dt>{t("task.dependencies")}</dt>
              <dd>{task.depends_on.length === 0 ? t("task.none") : task.depends_on.map((id) => (numbers.has(id) ? `#${numbers.get(id)}` : id.slice(0, 8))).join(", ")}</dd>
            </div>
            <div>
              <dt>{t("task.requirements")}</dt>
              <dd>{task.requirement_refs.length === 0 ? t("task.none") : task.requirement_refs.join(", ")}</dd>
            </div>
            <div>
              <dt>{t("task.dueAt")}</dt>
              <dd>{task.due_at ? formatDateTime(task.due_at) : "—"}</dd>
            </div>
            <div>
              <dt>{t("task.time")}</dt>
              <dd>{current?.durations ? <DurationsView durations={current.durations} /> : <span className="subtle">{t("task.notReportedYet")}</span>}</dd>
            </div>
            <div>
              <dt>{t("task.agentModel")}</dt>
              <dd>
                <RoutingSummary state={routing} />
              </dd>
            </div>
          </dl>

          {detailError ? (
            <ErrorBanner error={detailError} onRetry={() => setAttempt((n) => n + 1)} />
          ) : !current ? (
            <LoadingBlock label={t("common.loading")} lines={3} />
          ) : (
            <>
              {current.description ? (
                <section>
                  <h3 className="detail-heading">{t("task.descriptionField")}</h3>
                  <p className="user-content">{current.description}</p>
                </section>
              ) : null}
              <section>
                <h3 className="detail-heading">
                  {t("task.acceptance")}{" "}
                  <span className="subtle">
                    ({task.acceptance_summary.satisfied}/{task.acceptance_summary.total})
                  </span>
                </h3>
                {current.acceptance_criteria.length === 0 ? (
                  <p className="subtle">{t("task.noAcceptance")}</p>
                ) : (
                  <ul className="checklist">
                    {current.acceptance_criteria.map((c) => (
                      <li key={c.id}>
                        {c.satisfied ? <CheckIcon size={16} className="tone-success" /> : <CircleIcon size={16} className="subtle" />}
                        <span className="user-content">{c.text}</span>
                        <span className="sr-only">{c.satisfied ? t("task.satisfied") : t("task.notSatisfied")}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </>
          )}

          <TaskProductionSection taskId={task.id} version={version} />
          <CouncilReviewsSection taskId={task.id} version={version} />
          <RoutingDecisionSection taskId={task.id} state={routing} />

          <section>
            <h3 className="detail-heading">{t("task.timeline")}</h3>
            <TaskTimeline taskId={task.id} version={version} />
          </section>

          <section>
            <h3 className="detail-heading">{current ? t("kanban.requestAction") : t("kanban.suggestedActions")}</h3>
            <div className="row row--wrap">
              {candidateActions(task, current?.available_actions ?? null).map((a) => (
                <Button
                  key={a}
                  size="sm"
                  variant={a === "cancel" || a === "fail" ? "danger" : "secondary"}
                  onClick={() => {
                    onClose();
                    onAction(task, a);
                  }}
                >
                  {t(`action.${a}`)}
                </Button>
              ))}
            </div>
            <p className="field-hint">{t("kanban.serverDecides")}</p>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
